import request from '../../../../core/wrappers/axios-wrapper';
import { ApiURL } from '../../../../types/constants/apiUrls/api-urls';
import { RequestType } from '../../../../types/enums/request-enums';

export type ProduceStudioProductMediaRequest = {
    barcodes?: string[];
    batchSize?: number;
};

export type ProduceStudioProductMediaStatus = 'Skipped' | 'Completed' | 'Failed' | string;

export type ProduceStudioProductMediaResponse = {
    status: ProduceStudioProductMediaStatus;
    message?: string;
    batchSize?: number;
    producedBarcodeCount?: number;
    skippedBarcodeCount?: number;
    processedImageCount?: number;
    hasMore?: boolean;
};

export type SimulateBatchProcessCompleteRequest = {
    barcodes?: string[];
    batchSize?: number;
};

export type SimulateBatchProcessCompleteResponse = {
    status: 'Skipped' | 'Completed' | 'Failed' | string;
    message?: string;
    batchSize?: number;
    updatedImageCount?: number;
    processedPathSetCount?: number;
    hasMore?: boolean;
};

const pick = (raw: Record<string, unknown>, camel: string, pascal: string) =>
    raw[camel] ?? raw[pascal];

const pickString = (raw: Record<string, unknown>, key: string) =>
    String(pick(raw, key, key.charAt(0).toUpperCase() + key.slice(1)) ?? '');

const pickNumber = (raw: Record<string, unknown>, key: string) => {
    const value = pick(raw, key, key.charAt(0).toUpperCase() + key.slice(1));
    return value == null ? undefined : Number(value);
};

const pickBool = (raw: Record<string, unknown>, key: string) =>
    Boolean(pick(raw, key, key.charAt(0).toUpperCase() + key.slice(1)));

/** BFF Newtonsoft bazen enum'u sayı (1=Completed) gönderir. */
const normalizeProduceStatus = (value: unknown): ProduceStudioProductMediaStatus => {
    if (value == null) {
        return '';
    }
    if (typeof value === 'number') {
        switch (value) {
            case 0:
                return 'Skipped';
            case 1:
                return 'Completed';
            case 2:
                return 'Failed';
            default:
                return String(value);
        }
    }
    const text = String(value).trim();
    if (text === '1') {
        return 'Completed';
    }
    if (text === '0') {
        return 'Skipped';
    }
    if (text === '2') {
        return 'Failed';
    }
    return text;
};

const isProduceSuccess = (status: ProduceStudioProductMediaStatus) => status === 'Completed';

const mapProduceResponse = (raw: Record<string, unknown>): ProduceStudioProductMediaResponse => ({
    status: normalizeProduceStatus(pick(raw, 'status', 'Status')),
    message: pickString(raw, 'message') || undefined,
    batchSize: pickNumber(raw, 'batchSize'),
    producedBarcodeCount: pickNumber(raw, 'producedBarcodeCount'),
    skippedBarcodeCount: pickNumber(raw, 'skippedBarcodeCount'),
    processedImageCount: pickNumber(raw, 'processedImageCount'),
    hasMore: pickBool(raw, 'hasMore'),
});

const mapSimulateResponse = (raw: Record<string, unknown>): SimulateBatchProcessCompleteResponse => ({
    status: pickString(raw, 'status'),
    message: pickString(raw, 'message') || undefined,
    batchSize: pickNumber(raw, 'batchSize'),
    updatedImageCount: pickNumber(raw, 'updatedImageCount'),
    processedPathSetCount: pickNumber(raw, 'processedPathSetCount'),
    hasMore: pickBool(raw, 'hasMore'),
});

const Produce = async (
    data: ProduceStudioProductMediaRequest = {},
): Promise<ProduceStudioProductMediaResponse> => {
    const raw = (await request({
        url: ApiURL.Command.StudioCommandURLs.AiIntegrationCommandURL.Produce,
        method: RequestType.POST,
        data,
    })) as Record<string, unknown>;

    return mapProduceResponse(raw);
};

const SimulateBatchComplete = async (
    data: SimulateBatchProcessCompleteRequest = {},
): Promise<SimulateBatchProcessCompleteResponse> => {
    const raw = (await request({
        url: ApiURL.Command.StudioCommandURLs.AiIntegrationCommandURL.SimulateBatchComplete,
        method: RequestType.POST,
        data,
    })) as Record<string, unknown>;

    return mapSimulateResponse(raw);
};

export const AiIntegrationCommand = {
    Produce,
    SimulateBatchComplete,
    isProduceSuccess,
};
